/**
 * Live MFA status from Keycloak (+ durable UI cache).
 * Secrets are never returned — only type, label, createdDate.
 *
 * Source of truth for enrollment: Keycloak user credential store (IdP DB).
 * Browser session/localStorage is only a UI cache for card highlighting.
 */

import { defaultMfaDeviceLabel, flattenAccountCredentials } from './accountCredentialsFlatten'
import { getAuthConfig, isOidcConfigured } from './config'
import { decodeClaims, getAccessToken, isDemoSession } from './oidc'

const KEY_CACHE = 'w7_mfa_status_cache'
const KEY_LOCAL = 'w7_mfa_active_methods'

/** UI cards that all map to Keycloak's single `webauthn` credential type. */
export const WEBAUTHN_METHOD_IDS = ['passkey', 'fingerprint', 'security-key'] as const

/**
 * Sign-in second factors that must be mutually exclusive.
 * Recovery codes are a backup and may coexist with either.
 */
export const PRIMARY_MFA_IDS = ['totp', 'passkey'] as const
export type PrimaryMfaId = (typeof PRIMARY_MFA_IDS)[number]

/** Keycloak cannot tell fingerprint vs USB key apart — Active lives on `passkey` only. */
export function canonicalMfaMethodId(methodId: string): string {
  return (WEBAUTHN_METHOD_IDS as readonly string[]).includes(methodId) ? 'passkey' : methodId
}

export function isPrimaryMfaMethod(methodId: string): methodId is PrimaryMfaId {
  const c = canonicalMfaMethodId(methodId)
  return c === 'totp' || c === 'passkey'
}

/** The other primary factor, if this method is primary (totp ↔ passkey). */
export function rivalPrimaryMfaId(methodId: string): PrimaryMfaId | null {
  const c = canonicalMfaMethodId(methodId)
  if (c === 'totp') return 'passkey'
  if (c === 'passkey') return 'totp'
  return null
}

/** Which primary factor is currently on, if any. Prefer totp when both exist (legacy). */
export function activePrimaryMfaId(activeIds: Iterable<string>): PrimaryMfaId | null {
  const set = new Set([...activeIds].map(canonicalMfaMethodId))
  if (set.has('totp')) return 'totp'
  if (set.has('passkey')) return 'passkey'
  return null
}

function normalizeActiveIds(ids: string[]): string[] {
  const set = new Set(ids)
  const hasWebAuthn = WEBAUTHN_METHOD_IDS.some((id) => set.has(id))
  if (hasWebAuthn) {
    for (const id of WEBAUTHN_METHOD_IDS) set.delete(id)
    set.add('passkey')
  }
  return [...set]
}

export interface MfaCredentialInfo {
  id: string
  type: string
  userLabel?: string
  createdDate?: number
}

export interface MfaStatus {
  /** Method ids from mfaMethods that have at least one credential. */
  activeMethodIds: string[]
  credentials: MfaCredentialInfo[]
  /** true when credentials API answered with JSON. */
  fromAccountApi: boolean
  error?: string
}

function accountCredentialsUrl(): string | null {
  const cfg = getAuthConfig()
  if (!isOidcConfigured(cfg)) return null
  // DEV: Vite middleware normalizes Account/Admin → containers.
  // Prod: hit Account API with user-credentials so passkeys/OTP ids are included.
  if (import.meta.env.DEV) return '/api/account/credentials'
  return `${cfg.keycloakUrl}/realms/${cfg.realm}/account/credentials?user-credentials=true`
}

function storageKeyForUser(): string {
  const who = decodeClaims(getAccessToken())
  const id = who?.sub || who?.preferredUsername || 'anon'
  return `${KEY_LOCAL}:${id}`
}

/** Map Keycloak credential type → our method card ids (Active chips). */
export function methodIdsForCredentialType(type: string): string[] {
  const t = type.toLowerCase()
  if (t === 'otp' || t === 'totp' || t === 'hotp') return ['totp']
  // One WebAuthn credential must not light up three separate "Active" cards.
  if (t === 'webauthn' || t === 'webauthn-passwordless') return ['passkey']
  if (t.includes('recovery')) return ['recovery']
  return []
}

function readSessionCache(): MfaStatus | null {
  try {
    const raw = sessionStorage.getItem(KEY_CACHE)
    if (!raw) return null
    return JSON.parse(raw) as MfaStatus
  } catch {
    return null
  }
}

function writeSessionCache(status: MfaStatus): void {
  sessionStorage.setItem(KEY_CACHE, JSON.stringify(status))
}

function readLocalActiveIds(): string[] {
  try {
    if (typeof localStorage === 'undefined') return []
    const raw = localStorage.getItem(storageKeyForUser())
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    const ids = Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []
    return normalizeActiveIds(ids)
  } catch {
    return []
  }
}

function writeLocalActiveIds(ids: string[]): void {
  try {
    if (typeof localStorage === 'undefined') return
    localStorage.setItem(storageKeyForUser(), JSON.stringify(normalizeActiveIds(ids)))
  } catch {
    /* private mode / missing localStorage */
  }
}

function mergeActive(apiIds: string[], localIds: string[]): string[] {
  return normalizeActiveIds([...apiIds, ...localIds])
}

/** True when the id is a real Keycloak credential (not a local/synthetic placeholder). */
export function isDeletableCredentialId(id: string): boolean {
  const t = id.trim()
  if (!t || t.startsWith('local-')) return false
  // credentialsToContainers / synthetic fallbacks: otp-0, webauthn-1, …
  if (/^(otp|totp|hotp|webauthn|webauthn-passwordless|recovery)(-\d+)?$/i.test(t)) return false
  // Keycloak federated dummy ids (Account API when storage hides the real UUID).
  if (/^(otp|totp|hotp|webauthn|webauthn-passwordless|recovery)-id$/i.test(t)) return false
  return true
}

/** Drop a credential from the UI cache after a successful delete AIA. */
export function forgetCredential(credentialId: string): void {
  const prev = readSessionCache()
  if (!prev) return
  const credentials = prev.credentials.filter((c) => c.id !== credentialId)
  const active = new Set<string>()
  for (const c of credentials) {
    for (const mid of methodIdsForCredentialType(c.type)) active.add(mid)
  }
  const next: MfaStatus = {
    ...prev,
    credentials,
    activeMethodIds: [...active],
  }
  writeSessionCache(next)
  writeLocalActiveIds(next.activeMethodIds)
}

/**
 * Clear a stuck Active chip when Keycloak has no deletable credential
 * (common after passkey was removed elsewhere, or only a local-* placeholder remains).
 */
export function clearLocalMethodActive(methodId: string): void {
  const canonical = canonicalMfaMethodId(methodId)
  const nextIds = readLocalActiveIds().filter((id) => id !== canonical)
  writeLocalActiveIds(nextIds)
  const prev = readSessionCache()
  if (!prev) return
  const credentials = prev.credentials.filter(
    (c) => !methodIdsForCredentialType(c.type).includes(canonical),
  )
  const active = new Set<string>()
  for (const c of credentials) {
    for (const mid of methodIdsForCredentialType(c.type)) active.add(mid)
  }
  writeSessionCache({
    ...prev,
    credentials,
    activeMethodIds: [...active],
  })
}

/** After AIA return — mark a method active immediately for card highlight. */
export function markMethodEnrolled(methodId: string): void {
  const canonical = canonicalMfaMethodId(methodId)
  const local = readLocalActiveIds()
  writeLocalActiveIds([...local, canonical])

  const prev = readSessionCache() ?? {
    activeMethodIds: [],
    credentials: [],
    fromAccountApi: false,
  }
  prev.activeMethodIds = mergeActive(prev.activeMethodIds, [canonical, ...local])
  const label =
    methodId === 'totp'
      ? 'Authenticator app'
      : methodId === 'recovery'
        ? 'Recovery codes'
        : methodId === 'fingerprint'
          ? 'Fingerprint / device'
          : methodId === 'security-key'
            ? 'Security key'
            : 'Passkey'
  const type =
    canonical === 'totp' ? 'otp' : canonical === 'recovery' ? 'recovery-authn-codes' : 'webauthn'
  prev.credentials = [
    ...prev.credentials.filter((c) => !methodIdsForCredentialType(c.type).includes(canonical)),
    {
      id: `local-${canonical}`,
      type,
      userLabel: label,
      createdDate: Date.now(),
    },
  ]
  writeSessionCache(prev)
}

/** Build hub status from flat creds (Account metadatas or Admin / middleware containers). */
export function statusFromCredentials(raw: Array<{
  id?: string
  type?: string
  userLabel?: string
  createdDate?: number
}>): { credentials: MfaCredentialInfo[]; activeMethodIds: string[] } {
  const credentials: MfaCredentialInfo[] = []
  const active = new Set<string>()
  const typeCount = new Map<string, number>()
  for (const c of raw ?? []) {
    const type = String(c.type ?? '').toLowerCase()
    if (!type || type === 'password' || type === 'password-history') continue
    const id = String(c.id ?? '').trim()
    if (!id) continue
    const n = typeCount.get(type) ?? 0
    typeCount.set(type, n + 1)
    const label = (c.userLabel || '').trim() || defaultMfaDeviceLabel(type, n)
    credentials.push({
      id,
      type,
      userLabel: label,
      createdDate: typeof c.createdDate === 'number' ? c.createdDate : undefined,
    })
    for (const mid of methodIdsForCredentialType(type)) active.add(mid)
  }
  return { credentials, activeMethodIds: [...active] }
}

export async function fetchMfaStatus(): Promise<MfaStatus> {
  if (isDemoSession()) {
    return { activeMethodIds: [], credentials: [], fromAccountApi: false, error: 'demo' }
  }
  const localIds = readLocalActiveIds()
  const token = getAccessToken()
  const url = accountCredentialsUrl()
  if (!token || !url) {
    const cached = readSessionCache()
    return {
      activeMethodIds: mergeActive(cached?.activeMethodIds ?? [], localIds),
      credentials: cached?.credentials ?? [],
      fromAccountApi: false,
      error: 'no-session',
    }
  }

  try {
    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    })
    const text = await res.text()
    const looksJson = text.trim().startsWith('[') || text.trim().startsWith('{')
    if (!res.ok || !looksJson) {
      const cached = readSessionCache()
      return {
        activeMethodIds: mergeActive(cached?.activeMethodIds ?? [], localIds),
        credentials: cached?.credentials ?? [],
        fromAccountApi: false,
        error: `account-api-${res.status}`,
      }
    }
    const payload = JSON.parse(text) as unknown
    // Error envelopes (`{ message }`) must not look like “no MFA”.
    if (!Array.isArray(payload)) {
      const cached = readSessionCache()
      return {
        activeMethodIds: mergeActive(cached?.activeMethodIds ?? [], localIds),
        credentials: cached?.credentials ?? [],
        fromAccountApi: false,
        error: 'account-api-shape',
      }
    }
    const parsed = statusFromCredentials(flattenAccountCredentials(payload))
    // IdP is source of truth once the Account API answers — otherwise a removed
    // OTP/passkey would stay "Active" forever from markMethodEnrolled cache.
    writeLocalActiveIds(parsed.activeMethodIds)
    const status: MfaStatus = {
      activeMethodIds: parsed.activeMethodIds,
      credentials: parsed.credentials,
      fromAccountApi: true,
    }
    writeSessionCache(status)
    return status
  } catch {
    const cached = readSessionCache()
    return {
      activeMethodIds: mergeActive(cached?.activeMethodIds ?? [], localIds),
      credentials: cached?.credentials ?? [],
      fromAccountApi: false,
      error: 'network',
    }
  }
}
